import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faBookmark,
  faSave,
} from '@fortawesome/free-solid-svg-icons';
import CancelConfirm from './CancelConfirm';
import DriverOption from './DriverOption';
import ConflictWarning from './ConflictWarning';
import { checkAllTimeBasedConflicts } from './conflictUtils';
import './driver-option.css';
import moment from 'moment';

const DriverDropDown = ({
  drivers,
  groupId,
  updateDriver,
  groupDetails,
  assignAllDrivers,
  setLoadingCard,
  dateTime,
  bound,
  trips,
  tripsLength,
  onBulkConflictCheck,
  driverRef,
  tripReasonModalPortalTarget,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [confirmAssign, setConfirmAssign] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [singleTripAlert, SetSingleTripAlert] = useState(false);
  const [tripReason, setTripReason] = useState('');
  const [filteredDrivers, setFilteredDrivers] = useState(drivers.vehicles);
  const blockedUserIds = drivers.blocked_user_ids || [];
  const [conflictQueue, setConflictQueue] = useState([]);
  const [isForceAssign, setIsForceAssign] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);
  const [tripReasonPortalContainer, setTripReasonPortalContainer] = useState(null);
  const prevAssignAllDriversRef = useRef(assignAllDrivers);

  useEffect(() => {
    if (!tripReasonModalPortalTarget) return;
    const container = document.querySelector(tripReasonModalPortalTarget);
    if (!container) return;
    const currentPosition = getComputedStyle(container).position;
    // Browsers return 'static' for unset position; jsdom returns ''. Either way,
    // the container needs to be a positioning context so the modal's absolute
    // inset covers only its area (not the whole viewport).
    if (!currentPosition || currentPosition === 'static') {
      container.style.position = 'relative';
    }
    setTripReasonPortalContainer(container);
  }, [tripReasonModalPortalTarget]);

  const handleDropdownToggle = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleDriverSelect = (driver) => {
    setSelectedDriver(driver);
    setDropdownOpen(false);
    setTripReason('');
    SetSingleTripAlert(false);
  };

  const handleDriverAssign = () => {
    if (selectedDriver) {
      updateDriver(selectedDriver, groupId, tripReason, isForceAssign);
      setConfirmAssign(false);
      setLoadingCard(true);
      setIsForceAssign(false);
      setTripReason('');
      if (selectedDriver.id == -1) {
        setSelectedDriver(null);
      }
    }
  };

  useEffect(() => {
    if (groupDetails) {
      const savedDriver = drivers?.vehicles?.find(
        (driver) => driver?.user_id === groupDetails?.driver_id
      );
      if (savedDriver) {
        setSelectedDriver(savedDriver);
      }
    }
  }, []);

  useEffect(() => {
    if (assignAllDrivers > 0 && assignAllDrivers !== prevAssignAllDriversRef.current) {
      prevAssignAllDriversRef.current = assignAllDrivers;
      
      if (groupDetails?.user_id && selectedDriver && groupDetails?.user_id === selectedDriver.user_id) {
        handleDriverAssign();
        return;
      }

      if (onBulkConflictCheck) {
        const conflicts = checkAllTimeBasedConflicts({
          driver: selectedDriver,
          groupId,
          groupDetails,
          dateTime,
          bound,
          trips,
          getDriverTrips
        });

        onBulkConflictCheck(groupId, selectedDriver, conflicts, () => {
          handleDriverAssign();
        });
      } else {
        const conflicts = checkAllTimeBasedConflicts({
          driver: selectedDriver,
          groupId,
          groupDetails,
          dateTime,
          bound,
          trips,
          getDriverTrips
        });

        if (conflicts && conflicts.length > 0) {
          setConflictQueue(conflicts);
          setConfirmAssign(false);
          SetSingleTripAlert(false);
        } else {
          handleDriverAssign();
        }
      }
    }
  }, [assignAllDrivers]);

  useEffect(() => {
    driverRef.current = selectedDriver;
  }, [selectedDriver, driverRef]);

  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle the search query and update the filtered drivers list
  useEffect(() => {
    const updatedFilteredDrivers = drivers?.vehicles?.filter((driver) => {
      const driverName = `${driver.username} ${driver.usersurname}`.toLowerCase();
      const matchesSearch = driverName.includes(searchQuery.toLowerCase());

      if (!matchesSearch) return false;

      const tripLength = typeof trips === 'number' ? trips : trips.length;

      return driver.car_occupancy >= tripLength;
    });

    setFilteredDrivers(updatedFilteredDrivers);
  }, [searchQuery, drivers, trips]);

  const alreadyAssigned = (driver) => {
    const driverTrips = getDriverTrips(driver.user_id);
    const card_trip_type = bound === 'Inbound' ? 0 : 1;

    let currentCompanyAddress = groupDetails?.company_address;

    if (!currentCompanyAddress && trips && trips.length > 0) {
      currentCompanyAddress =
        bound === 'Inbound' ? trips[0]?.destination : trips[0]?.location;
    }

    if (driverTrips && groupDetails) {
      return driverTrips.some((trip) => {
        if (
          trip.date === groupDetails.date &&
          trip.timeslot === groupDetails.timeslot
        ) {
          if (
            currentCompanyAddress &&
            trip.company_address &&
            currentCompanyAddress === trip.company_address &&
            (
              (card_trip_type === 0 && trip.trip_type === 1) ||
              (card_trip_type === 1 && trip.trip_type === 0)
            )
          ) {
            return false;
          }
          return true;
        }
        return false;
      });
    }

    if (driverTrips && dateTime) {
      const [date, time] = dateTime.split(' - ');
      return driverTrips.some((trip) => {
        const tripDate = moment(trip.date);
        const cardDate = moment(date, 'DD/MM/YYYY');

        if (tripDate.isSame(cardDate, 'date') && trip.timeslot === time) {
          if (
            currentCompanyAddress &&
            trip.company_address &&
            currentCompanyAddress === trip.company_address &&
            (
              (card_trip_type === 0 && trip.trip_type === 1) ||
              (card_trip_type === 1 && trip.trip_type === 0)
            )
          ) {
            return false;
          }
          return true;
        }
        return false;
      });
    }

    return false;
  };

  const getSelectedDriver = () => {
    if (selectedDriver) {
      return selectedDriver.username + ' ' + selectedDriver.usersurname;
    } else {
      return 'Driver';
    }
  };

  const getDriverTrips = (user_id) => {
    const driverTrips = drivers.grouped_trips[user_id];
    if (driverTrips) {
      return driverTrips;
    }
    return null;
  };

  // Personal-event occurrences for the driver (BE-08b) — empty server-side
  // when the flag is off, so the pills simply don't appear.
  const getDriverEvents = (user_id) => {
    return drivers.driver_events?.[user_id] ?? null;
  };

  const handleConfirm = () => {
    setDropdownOpen(false);
    if (selectedDriver) {
      // Skip conflict checking if trip already has the same driver assigned (Assigned tab)
      if (groupDetails?.user_id && groupDetails?.user_id === selectedDriver.user_id) {
        if (tripsLength == 1) {
          SetSingleTripAlert(true);
        } else {
          setConfirmAssign(true);
          SetSingleTripAlert(false);
        }
        return;
      }

      const conflicts = checkAllTimeBasedConflicts({
        driver: selectedDriver,
        groupId,
        groupDetails,
        dateTime,
        bound,
        trips,
        getDriverTrips
      });
      if (conflicts && conflicts.length > 0) {
        setConflictQueue(conflicts);
        // Wait for user to acknowledge all warnings
        SetSingleTripAlert(false);
        setConfirmAssign(false);
      } else {
        setConflictQueue([]);
        if (tripsLength == 1) {
          SetSingleTripAlert(true);
        } else {
          setConfirmAssign(true);
          SetSingleTripAlert(false);
        }
      }
    } else {
      setConfirmAssign(false);
      SetSingleTripAlert(false);
    }
  };

  const reasons = [
    { id: 1, title: 'Adhoc Passenger', value: 'Adhoc Passenger' },
    { id: 2, title: 'Adhoc Trip', value: 'Adhoc trip' },
    { id: 3, title: 'Late Booking', value: 'late booking' },
    { id: 4, title: 'Only Trip in Timeslot', value: 'only trip in timeslot' },
    {
      id: 5,
      title: 'Other Passenger Cancelled',
      value: 'other passenger cancelled',
    },
    { id: 6, title: 'Outlier', value: 'outlier' },
  ];

  const handleTripReasonChange = (event) => {
    setTripReason(event.target.value);
  };

  return (
    <div className="w-full flex overflow-visible">
      <div
        className={`h-5 max-h-20 w-full px-2 py-2 text-gray-700 flex items-center justify-between focus:outline-none rounded-l-full border border-gray-700 ${
          groupDetails?.status == 'complete'
            ? 'bg-gray-200 cursor-not-allowed'
            : 'bg-gray-100 hover:bg-gray-200'
        }  `}
        onClick={groupDetails?.status == 'complete' ? {} : handleDropdownToggle}
        data-testid={'driver-dropdown'}>
        <span className=" ml-2 text-xs font-semibold">
          {getSelectedDriver()}
        </span>
        <FontAwesomeIcon icon={faChevronDown} size={'xs'} />
      </div>
      <div
        disabled={singleTripAlert}
        onClick={groupDetails?.status == 'complete' ? {} : handleConfirm}
        data-testid={'save-btn'}
        className={`max-h-20 px-2 py-2 rounded-r-full w-8 h-5 border border-gray-700 flex items-center justify-between focus:outline-none text-gray-700 ${
          groupDetails?.status == 'complete'
            ? 'bg-gray-200 cursor-not-allowed'
            : 'bg-gray-100 hover:bg-gray-200'
        } `}>
        <FontAwesomeIcon icon={faSave} size="xs" className="mr-1" />
      </div>
      {dropdownOpen && (
        <div
          className="absolute z-10 mt-5 w-64 bg-white shadow-lg rounded-md overflow-y-scroll scrollable-dropdown"
          style={{ height: 'auto', maxHeight: '120px' }}>
          <input
            type="text"
            autoFocus
            className="px-2 py-1 text-xs text-gray-700 w-full"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchInputChange}
          />
          <div
            className="py-1"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
            aria-valuenow={selectedDriver}>
            {[
              // Filter blocked drivers for company
              { id: -1, user_id: -1, username: 'Reject', usersurname: 'trip' },
              ...filteredDrivers,
            ].map((driver, index) => (
              <DriverOption
                data-testid={`option-${driver.id}`}
                key={`driver-${driver.id}-${index}`}
                setSelectedDriver={handleDriverSelect}
                driver={{ ...driver, isBlocked: blockedUserIds.includes(driver.user_id) }}
                driverTrips={index == 0 ? null : getDriverTrips(driver.user_id)}
                driverEvents={index == 0 ? null : getDriverEvents(driver.user_id)}
                groupDetails={groupDetails}
                dateTime={dateTime}
                alreadyAssigned={index == 0 ? false : alreadyAssigned(driver)}
              />
            ))}
          </div>
        </div>
      )}
      {confirmAssign && (
        <>
          <div
            style={{ zIndex: 10 }}
            className="absolute top-0 left-0 w-full h-full bg-black opacity-50"
            onClick={() => setConfirmAssign(false)}></div>
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <CancelConfirm
              data-testid={'confirm-assign'}
              title={'Assign Driver'}
              body={
                'You are about to assign a driver and send out communications. Are you sure?'
              }
              onNoClick={() => {
                setConfirmAssign(false);
              }}
              onYesClick={() => {
                handleDriverAssign();
              }}
            />
          </div>
        </>
      )}

      {singleTripAlert && (() => {
        const tripReasonModal = (
          <>
            <div
              style={{ zIndex: 39 }}
              className="absolute top-0 left-0 w-full h-full bg-black opacity-50"
              onClick={() => {
                SetSingleTripAlert(false);
                setTripReason('');
                setIsForceAssign(false);
              }}
            ></div>
            <div className="absolute z-40 top-0 left-0 w-full h-full flex items-center justify-center">
              <div className="relative bg-white rounded-md shadow-lg p-6">
                <h2 className="text-xl text-gray-700 font-semibold mb-4">
                  Select Trip Reason:
                </h2>
                <select
                  value={tripReason}
                  onChange={handleTripReasonChange}
                  className="w-1/2 p-2 border rounded-md mb-4 pr-2">
                  <option value="">Select Trip Reason</option>
                  {reasons.map((reason) => (
                    <option key={reason.id} value={reason.value}>
                      {reason.title}
                    </option>
                  ))}
                </select>
                <button
                  disabled={!singleTripAlert || !tripReason}
                  onClick={() => {
                    if (tripReason) {
                      handleDriverAssign();
                      SetSingleTripAlert(false);
                    }
                  }}
                  className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed">
                  Submit
                </button>
                <button
                  disabled={!singleTripAlert}
                  onClick={() => {
                    SetSingleTripAlert(false);
                    setTripReason('');
                    setIsForceAssign(false);
                  }}
                  className="ml-2 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600">
                  Cancel
                </button>
              </div>
            </div>
          </>
        );
        return tripReasonModalPortalTarget && tripReasonPortalContainer
          ? createPortal(tripReasonModal, tripReasonPortalContainer)
          : tripReasonModal;
      })()}

      {conflictQueue.length > 0 && (
        <ConflictWarning
          conflictQueue={conflictQueue}
          onDontAssign={() => {
            setConflictQueue([]);
            setSelectedDriver(null);
          }}
          onSkipCurrent={() => {
            setConflictQueue([]);
            setSelectedDriver(null);
          }}
          onContinue={() => {
            setConflictQueue([]);
            setIsForceAssign(true);
            if (tripsLength == 1) {
              SetSingleTripAlert(true);
            } else {
              handleDriverAssign();
            }
          }}
        />
      )}
    </div>
  );
};

export default DriverDropDown;
